import { FetchResult } from '@apollo/client';
import { TRIAL_MEMBERSHIP_ACTIVATED_AT_KEY } from '@provision/service-level-utils';
import { useMemo } from 'react';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import {
  Address,
  PartnerGroupRules,
  ProjectStages,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import { useCustomAttributeValues } from 'src/graphql/entities/custom-attributes/hooks/use-custom-attribute-values';
import { useStripeBillingPortalUrl } from 'src/graphql/entities/payment-portal-url';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import {
  CreateStripeSubscriptionScheduleMutation,
  ScheduleCreationParams,
} from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useShowModals } from 'src/store/modals';
import { useCopyToClipboard } from 'src/utils/hooks';

const LUCIDE_ICON_LIBRARY: IconLibrary = 'lucide';

export const useMembershipActions = ({
  address,
  conversationId,
  subscriptionDetails,
  addCppToSchedule,
  addPsmToSchedule,
  onUnpauseSubscription,
  isCppDisabled,
}: {
  address: Address;
  conversationId?: string;
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
  addCppToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  addPsmToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  onUnpauseSubscription?: () => void;
  isCppDisabled?: boolean;
}) => {
  const { generateStripeBillingPortalUrl } = useStripeBillingPortalUrl();
  const [
    showFollowUpModal,
    showComplimentaryPeriodModal,
    showActivateTrialMembershipModal,
    showPreSoldMembershipModal,
    showSendMembershipEmailModal,
    showCustomSignupLinkModal,
    showPauseSubscriptionModal,
  ] = useShowModals(
    Modals.MemberServicesFollowUp,
    Modals.AddComplimentaryPeriod,
    Modals.ActivateTrialMembership,
    Modals.AddPreSoldMembership,
    Modals.SendMembershipEmail,
    Modals.CustomSignupLink,
    Modals.PauseSubscription,
  );

  const { copyToClipboard } = useCopyToClipboard();
  const { partnerGroupRules } = usePartnerGroupRules(address.partner?.id ?? address.partnerId);
  const { groupRules } = useGroupRules();

  const hasAccessToSubscriptionManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];
  const hasAccessToSubscriptionManagement =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement];
  const hasAccessToProvisionProjects =
    partnerGroupRules[PartnerGroupRules.AccessToProvisionProjects];
  const canUpdateSLAs = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);
  const canGenerateCustomSignupLink = Boolean(
    groupRules[UserGroupRules.AccessToGenerateCustomCheckoutUrl],
  );
  const canPauseSubscription = Boolean(groupRules[UserGroupRules.AccessToPauseSubscription]);

  // One-time trial activation (1V-5895): user rule AND the partner is in the
  // pilot AND the address is a non-member. The stamp query below only runs
  // once those cheap gates pass.
  const canActivateTrialMembership =
    Boolean(groupRules[UserGroupRules.HasAccessToActivateMembershipTrial]) &&
    Boolean(partnerGroupRules[PartnerGroupRules.AccessToOneTimeMembershipActivationPilot]) &&
    !address?.platformSlaId;
  const { parsedValues: addressAttributeValues, loading: loadingTrialStamp } =
    useCustomAttributeValues({
      entity: 'Address',
      entityId: canActivateTrialMembership ? address?.addressId : undefined,
    });
  const hasTrialMembershipStamp = Boolean(
    addressAttributeValues[TRIAL_MEMBERSHIP_ACTIVATED_AT_KEY],
  );

  const topStageProjectForAddress = useMemo(() => {
    const projects = address?.projects;
    if (!projects?.length) return null;
    return Math.max(...projects.map((project) => project?.projectStageId)) || null;
  }, [address?.projects]);

  const showAddPreSoldMembershipOption = useMemo(
    () =>
      canUpdateSLAs &&
      hasAccessToSubscriptionManagement &&
      !address?.preSoldMemberships?.[0]?.isScheduled &&
      (!hasAccessToProvisionProjects ||
        (hasAccessToProvisionProjects &&
          topStageProjectForAddress === ProjectStages.ServiceClient)),
    [
      address?.preSoldMemberships,
      canUpdateSLAs,
      hasAccessToProvisionProjects,
      hasAccessToSubscriptionManagement,
      topStageProjectForAddress,
    ],
  );

  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address),
    [address],
  );

  return useMemo(
    () => ({
      accessPaymentPortal: {
        label: 'Access payment portal',
        icon: 'credit-card',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!address?.addressId && hasAccessToSubscriptionManagement,
        handler: async () => {
          if (address?.addressId) {
            const url = await generateStripeBillingPortalUrl({ addressId: address.addressId });
            if (url?.url) {
              void copyToClipboard(url.url);
            }
          }
        },
      },
      sendMembershipEmail: {
        label: 'Send membership email',
        icon: 'mail',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!address?.addressId,
        handler: () => {
          showSendMembershipEmailModal({ address });
        },
      },
      followUp: {
        label: 'Member Services follow-up',
        icon: 'zap',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: hasAccessToSubscriptionManagementPlus,
        handler: () => {
          showFollowUpModal({ addressId: address?.addressId, conversationId });
        },
      },
      addComplimentaryPeriod: {
        label: 'Add complimentary period',
        icon: 'shield-check',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: hasAccessToSubscriptionManagement && canUpdateSLAs && !isCppDisabled,
        handler: () => {
          showComplimentaryPeriodModal({ address, addCppToSchedule });
        },
      },
      activateTrialMembership: {
        label: 'Activate trial membership',
        icon: 'id-card',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          canActivateTrialMembership && !loadingTrialStamp && !hasTrialMembershipStamp,
        handler: () => {
          showActivateTrialMembershipModal({ address });
        },
      },
      addPreSoldMembership: {
        label: 'Add pre-sold membership',
        icon: 'receipt',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: showAddPreSoldMembershipOption,
        handler: () => {
          showPreSoldMembershipModal({
            addressId: address?.addressId,
            addPsmToSchedule,
          });
        },
      },
      customSignupLink: {
        label: 'Custom signup link',
        icon: 'link',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        testId: 'membership-info-custom-signup-link-action',
        isAllowed:
          !!address?.addressId && !addressSubscriptionId && canGenerateCustomSignupLink,
        handler: () => {
          showCustomSignupLinkModal({
            address,
            addressSubscriptionId,
          });
        },
      },
      copyCalendlyLink: {
        label: 'Copy Member Services Calendly link',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          !!address?.partner?.membershipSalesCalendlyLink &&
          hasAccessToSubscriptionManagementPlus,
        handler: () => {
          if (address?.partner?.membershipSalesCalendlyLink) {
            void copyToClipboard(address.partner.membershipSalesCalendlyLink);
          }
        },
      },
      copySubscriptionId: {
        label: 'Copy subscription ID',
        icon: 'copy',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed: !!addressSubscriptionId,
        handler: () => {
          void copyToClipboard(addressSubscriptionId || '');
        },
      },
      pauseSubscription: {
        label: 'Pause subscription',
        icon: 'circle-pause',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          !!addressSubscriptionId &&
          hasAccessToSubscriptionManagement &&
          canUpdateSLAs &&
          canPauseSubscription &&
          !subscriptionDetails?.paused,
        handler: () => {
          showPauseSubscriptionModal({ address });
        },
      },
      unpauseSubscription: {
        label: 'Unpause subscription',
        icon: 'circle-play',
        iconLibrary: LUCIDE_ICON_LIBRARY,
        isAllowed:
          !!addressSubscriptionId &&
          hasAccessToSubscriptionManagement &&
          canUpdateSLAs &&
          canPauseSubscription &&
          !!subscriptionDetails?.paused,
        handler: () => {
          onUnpauseSubscription?.();
        },
      },
    }),
    [
      address,
      canActivateTrialMembership,
      hasTrialMembershipStamp,
      loadingTrialStamp,
      showActivateTrialMembershipModal,
      canUpdateSLAs,
      canGenerateCustomSignupLink,
      conversationId,
      copyToClipboard,
      generateStripeBillingPortalUrl,
      canPauseSubscription,
      hasAccessToSubscriptionManagement,
      hasAccessToSubscriptionManagementPlus,
      showAddPreSoldMembershipOption,
      showComplimentaryPeriodModal,
      addCppToSchedule,
      addPsmToSchedule,
      showFollowUpModal,
      showPreSoldMembershipModal,
      showSendMembershipEmailModal,
      showCustomSignupLinkModal,
      showPauseSubscriptionModal,
      addressSubscriptionId,
      subscriptionDetails,
      onUnpauseSubscription,
      isCppDisabled,
    ],
  );
};
